/**
 * AZP-7074 — one rating row per product card.
 *
 * "Amazon Reviews by Appio" scans the page for `a[href*="/products/"]` and
 * injects its own `.appio-rating-badge` next to every product link it finds,
 * which on this theme lands inside the card's `<h3 class="card-heading">`.
 * Cards that already render the theme's own rating (snippets/rating.liquid)
 * therefore show two star rows, and the compact cards — which have no room for
 * a rating at all in the design — get their title/price layout pushed apart.
 *
 * Appio also re-scans on demand: assets/scripts.js calls `Appio.checkRatings()`
 * after a tab switch so badges appear on tabs 2..n, and that pass injects a
 * SECOND badge into cards that already carry one. A few tab clicks and the
 * Recently Viewed / New Arrivals strips are stacked with them.
 *
 * So: take Appio's numbers — they are the ones the PDP shows, the Shopify
 * `reviews.*` metafield the Liquid fallback reads is never written by Appio —
 * paint them into the card's own rating row, and drop the injected badge.
 * Cards with no rating row (the compact strips) just lose the badge.
 *
 * The cart does the same thing for line items in cart-warehouse-resolver.js.
 */
(function () {
  "use strict";

  var SCALE_MAX = 5;

  function reviewsLabel(count) {
    var template = (window.productsStrings || {}).reviewsCount;
    if (typeof template === "string" && template.indexOf("[count]") !== -1) {
      return template.replace("[count]", String(count));
    }
    return count + " reviews";
  }

  /**
   * Repaint the card's existing star SVGs. They are already in the DOM with the
   * right viewBox and sizing, so only the modifier class and the path fill
   * change — no SVG markup is duplicated here.
   */
  function paintStars(starsEl, rating) {
    if (!starsEl) return;
    var stars = starsEl.querySelectorAll(".icon-rating-star");
    if (stars.length !== SCALE_MAX) return;

    var floor = Math.floor(rating);
    if (floor < 0) floor = 0;
    if (floor > SCALE_MAX) floor = SCALE_MAX;
    var decimal = rating - floor;
    var showHalf = decimal >= 0.3 && decimal <= 0.7 && floor < SCALE_MAX;
    if (decimal > 0.7 && floor < SCALE_MAX) floor += 1;

    for (var i = 0; i < stars.length; i += 1) {
      var kind = "empty";
      if (i < floor) kind = "full";
      else if (showHalf && i === floor) kind = "half";

      stars[i].setAttribute("class", "icon-rating-star icon-rating-star-" + kind);
      var path = stars[i].querySelector("path");
      if (path) {
        path.setAttribute(
          "fill",
          kind === "half" ? "url(#rating-star-gradient-half)" : "currentColor",
        );
      }
    }
  }

  function applyBadgeToCard(card, badge) {
    var link = card.querySelector(".card-rating .star-rating-link");
    if (!link) return;

    var rating = Number(badge.getAttribute("data-rating"));
    var raters = Number(badge.getAttribute("data-raters"));
    if (!isFinite(rating) || !isFinite(raters) || raters <= 0) return;

    link.setAttribute("data-average-rating", String(rating));
    paintStars(link.querySelector(".rating-stars"), rating);

    var caption = link.querySelector(".rating-caption");
    if (caption) caption.textContent = reviewsLabel(raters);
  }

  /**
   * AZP-7378 — the PDP badge (`.appio-rating-major`) renders stars and
   * "(N reviews)" but no average. Figma 4021:14943 puts the average between
   * them, so read it from Appio's own popup header (or `data-rating` when the
   * badge carries one) and insert it before the count. Appio can re-render
   * the badge, which drops the span; the observer below puts it back.
   */
  function addMajorBadgeAverage(badge) {
    var raters = badge.querySelector(".appio-rating-raters");
    if (!raters) return;

    var prev = raters.previousElementSibling;
    if (prev && prev.classList.contains("bs-rating-average")) return;

    var score = badge.querySelector(".appio-popup-header-score span");
    var rating = Number(badge.getAttribute("data-rating") || (score && score.textContent));
    if (!isFinite(rating) || rating <= 0) return;

    var average = document.createElement("span");
    average.className = "bs-rating-average";
    average.textContent = String(Math.round(rating * 10) / 10);
    raters.parentNode.insertBefore(average, raters);
  }

  function sync(root) {
    var scope = root && root.querySelectorAll ? root : document;

    var majors = scope.querySelectorAll(".appio-rating-badge.appio-rating-major");
    for (var m = 0; m < majors.length; m += 1) addMajorBadgeAverage(majors[m]);

    var badges = scope.querySelectorAll(".appio-rating-badge:not(.appio-rating-major)");

    for (var i = 0; i < badges.length; i += 1) {
      var badge = badges[i];
      var card = badge.closest ? badge.closest("product-card, .card-product") : null;
      if (!card) continue;

      applyBadgeToCard(card, badge);
      badge.remove();
    }
  }

  var scheduled = false;

  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      sync(document);
    }, 50);
  }

  function start() {
    sync(document);

    if (typeof MutationObserver === "undefined") return;

    // Appio injects after paint and again on every `checkRatings()` call, so the
    // sweep has to run for the life of the page, not once on load.
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        if (mutations[i].addedNodes.length) {
          scheduleSync();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.syncCardReviewBadges = sync;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
